import {observable} from 'mobx';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import uniqid from 'uniqid';

import {OdtReportBuildInstructions} from '../types/Table';
import {serialize} from '../utility/ApiParamConversion';
import * as ReportAPI from '../apis/ReportAPI';
import {getLanguage} from '../i18n/i18n';
import {isKPI, KPI} from '../kpi';
import {BuildingBlockType, PageBreakBuildingBlock, PageLayout, ReportBuildingBlock, SerializedReportBuildingBlock} from '../report/types/Report';
import {DateUtils} from '../utility/DateUtils';
import {DataKey} from './GeneralSelectorTableStore';
import {AnalyticsTab} from './store';

export interface DiagramInstruction {
    kpi: KPI;
    channel: string;
    color?: string;
}

export const isDiagramInstruction = (value: any): value is DiagramInstruction => {
    return value.kpi && isKPI(value.kpi) && value.channel && typeof value.channel === 'string';
};

export interface SearchDiagramInstruction extends DiagramInstruction {
    searchterm?: string;
}

export interface ProductDiagramInstruction extends DiagramInstruction {
    productId?: string;
}

export const isSearchDiagramInstruction = (value: any): value is SearchDiagramInstruction => {
    return value.searchterm && (value.searchterm === undefined || typeof value.searchterm === 'string') && isDiagramInstruction(value);
};

export const isProductDiagramInstruction = (value: any): value is ProductDiagramInstruction => {
    return value.productId && (value.productId === undefined || typeof value.productId === 'string') && isDiagramInstruction(value);
};

export interface OdtExportState {
    reportBuildInstructions: OdtReportBuildInstructions[];
    reportIsLoading: boolean;
    reportModalIsOpen: boolean;
    reportModalHeader: string;
    reportModalFooter: string;
    reportTargetCurrency: string;
    generateOdtFromBackend: () => void;
    clear: () => void;
}

class ExportStore implements OdtExportState {
    @observable public reportBuildInstructions: OdtReportBuildInstructions[] = [];
    @observable public reportIsLoading = false;
    @observable public reportModalIsOpen = false;
    @observable public reportTargetCurrency = '';
    public reportModalHeader = '';
    public reportModalFooter = '';

    constructor(private visibleDefaultColumns: {[pageLayout in PageLayout]: {[analyticsTab in AnalyticsTab]?: DataKey[]}}) {}

    public clear = () => {
        this.reportModalIsOpen = false;
        this.reportModalFooter = '';
        this.reportModalHeader = '';
        this.reportTargetCurrency = '';
    };

    private getTargetCurrency() {
        if (this.reportBuildInstructions.length === 0) {
            throw Error('Cannot get target currency, if no reportBuildInstructions are present');
        }
        return this.reportBuildInstructions[0].targetCurrency;
    }

    public generateOdtFromBackend = () => {
        this.reportIsLoading = true;

        const dateRangesWithId = _.uniqWith(this.reportBuildInstructions, (a, b) => DateUtils.dateRangeIsSame(a.interval, b.interval)).map(
            (uniqueBuildInstruction) => ({
                id: uniqid(),
                dateRange: uniqueBuildInstruction.interval,
            })
        );

        const pages = this.reportBuildInstructions.map((instruction): SerializedReportBuildingBlock[] => {
            const idNamedDateRange = dateRangesWithId.find((namedDateRange) =>
                DateUtils.dateRangeIsSame(namedDateRange.dateRange, instruction.interval)
            );
            if (!idNamedDateRange) {
                throw Error('Could not determine dateRange Id for diagramTable buildingBlock');
            }
            return [
                {
                    blockType: BuildingBlockType.TEXT,
                    id: uniqid(),
                    text: instruction.heading,
                    textStyle: {
                        italic: false,
                        underlined: false,
                        bold: true,
                        fontSize: 24,
                    },
                },
                {
                    blockType: BuildingBlockType.TEXT,
                    id: uniqid(),
                    text: instruction.header,
                    textStyle: {
                        italic: false,
                        underlined: false,
                        bold: false,
                        fontSize: 12,
                    },
                },
                {
                    blockType: BuildingBlockType.DIAGRAM_TABLE,
                    id: uniqid(),
                    idDate: idNamedDateRange.id,
                    searchDiagrams: instruction.searchDiagrams,
                    productDiagrams: instruction.productDiagrams,
                    columns: this.visibleDefaultColumns[PageLayout.LANDSCAPE][
                        instruction.searchDiagrams.length > 0 ? AnalyticsTab.SearchView : AnalyticsTab.ProductView
                    ],
                },
                {
                    blockType: BuildingBlockType.TEXT,
                    id: uniqid(),
                    text: instruction.footer,
                    textStyle: {
                        italic: false,
                        underlined: false,
                        bold: false,
                        fontSize: 12,
                    },
                },
            ];
        });

        const flattenAndJoinByBreak = (all: ReportBuildingBlock[], current: ReportBuildingBlock[], index: number): ReportBuildingBlock[] => {
            const pageBreak: PageBreakBuildingBlock[] = index === 0 ? [] : [{id: uniqid(), blockType: BuildingBlockType.PAGE_BREAK}];
            return [...all, ...pageBreak, ...current];
        };

        ReportAPI.downloadReport({
            currency: this.getTargetCurrency(),
            variables: [],
            dates: dateRangesWithId.map((item) => serialize(item, ['dateRange'])),
            layout: PageLayout.LANDSCAPE,
            language: getLanguage(),
            blocks: pages.reduce(flattenAndJoinByBreak, []),
        })
            .then((resp) => {
                fileDownload(resp.data, 'report.odt');
                this.reportIsLoading = false;
            })
            .catch(() => {
                this.reportIsLoading = false;
            });
        this.reportBuildInstructions = [];
    };
}

export default ExportStore;



// WEBPACK FOOTER //
// ./src/stores/OdtExportStore.ts